import { TrashIcon } from '@heroicons/react/24/outline';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Spinner } from '@/components/ui/spinner';
import { StatusChip } from '@/components/ui/status-chip';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ShareListItem, useGetShares, useRevokeShare } from '@/hooks/useShare';

interface ManageSharesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Forwarded to the share hooks for impersonation cache isolation. */
  email?: string;
}

// `expires_at` is an absolute unix epoch (seconds). Render it human-readably
// without pulling in the relative-time formatter (which expects RFC3339).
function formatExpiry(epochSeconds: number): string {
  if (!epochSeconds) return '—';
  return new Date(epochSeconds * 1000).toLocaleString();
}

// A share's effective state: a revoked flag wins, otherwise expiry is compared
// against now.
function shareState(share: ShareListItem): 'revoked' | 'expired' | 'active' {
  if (share.revoked) return 'revoked';
  if (share.expires_at && share.expires_at * 1000 < Date.now())
    return 'expired';
  return 'active';
}

const STATE_LABEL: Record<ReturnType<typeof shareState>, string> = {
  active: 'Active',
  expired: 'Expired',
  revoked: 'Revoked',
};

// StatusChip variants: active = info (blue/positive), expired/revoked = muted
// (inactive). No raw token is ever part of this map — there's nothing to leak.
const STATE_VARIANT: Record<ReturnType<typeof shareState>, 'info' | 'muted'> = {
  active: 'info',
  expired: 'muted',
  revoked: 'muted',
};

/**
 * Lists the current user's own share links and lets them revoke an active one.
 *
 * GET /share is owner-scoped server-side and returns the opaque, non-secret
 * `id` handle (not the raw token); DELETE /share revokes by that `id`. Nothing
 * here renders or stores the raw token (the payload has none) — the `id` is an
 * internal handle and is never shown to the user.
 */
export function ManageSharesDialog({
  open,
  onOpenChange,
  email,
}: ManageSharesDialogProps) {
  const {
    data: shares = [],
    isPending,
    isError,
    invalidate: invalidateShares,
  } = useGetShares(email, { enabled: open });

  const {
    mutate: revokeShare,
    isPending: isRevoking,
    variables: revoking,
  } = useRevokeShare(email);

  const handleRevoke = (id: string) => {
    revokeShare(
      { id },
      {
        // On success refetch GET /share so the row flips to its revoked state.
        onSuccess: () => {
          toast.success('Share link revoked');
          invalidateShares();
        },
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Manage shares</DialogTitle>
          <DialogDescription>
            Share links you have created. Revoke a link to make it stop
            resolving.
          </DialogDescription>
        </DialogHeader>

        {isPending ? (
          <div className="flex items-center justify-center py-10">
            <Spinner centered size="xl" />
          </div>
        ) : isError ? (
          <p className="text-chariot-text-secondary py-10 text-center text-sm">
            Failed to load share links.
          </p>
        ) : shares.length === 0 ? (
          <p className="text-chariot-text-secondary py-10 text-center text-sm">
            You have not created any share links yet.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>File</TableHead>
                <TableHead>Expires</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {shares.map(share => {
                const state = shareState(share);
                return (
                  <TableRow key={share.id}>
                    <TableCell className="max-w-xs truncate font-medium">
                      {share.filename}
                    </TableCell>
                    <TableCell className="text-chariot-text-secondary text-sm">
                      {formatExpiry(share.expires_at)}
                    </TableCell>
                    <TableCell>
                      <StatusChip
                        variant={STATE_VARIANT[state]}
                        label={STATE_LABEL[state]}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        // Already-revoked rows have nothing to revoke; disable
                        // the in-flight row so a double-click can't re-fire.
                        disabled={
                          state === 'revoked' ||
                          (isRevoking && revoking?.id === share.id)
                        }
                        aria-label={`Revoke share for ${share.filename}`}
                        onClick={() => handleRevoke(share.id)}
                      >
                        <TrashIcon className="text-chariot-stroke-critical size-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </DialogContent>
    </Dialog>
  );
}
